import { useMemo } from 'react';
import { getBrowserTimezone } from '../../utils/get-browser-timezone';
import baseLogo from './logos/omnidots-logo.svg';
import {
    CountryGroup,
    HOLIDAY_THEMES,
    getSeasonalThemeDecision,
    getThemeCountries,
    type SeasonalThemeEntry,
} from './seasonalTheming';

type CountryCode = string;

export { CountryGroup };

interface SeasonalLogoEntry {
    altText: string;
    matcher: SeasonalThemeEntry['matcher'];
    countries?: CountryCode[];
    countryGroups?: CountryGroup[];
    asset: string;
}

export interface SeasonalLogoResult {
    altText: string;
    src: string;
    countries: CountryCode[];
}

export interface SeasonalLogoContext {
    date: Date;
    timezone?: string;
}

function toSeasonalLogoEntry(theme: SeasonalThemeEntry): SeasonalLogoEntry | null {
    if (!theme.logo) {
        return null;
    }
    return {
        altText: theme.logo.altText,
        matcher: theme.matcher,
        countries: theme.countries,
        countryGroups: theme.countryGroups,
        asset: theme.logo.asset,
    };
}

function toSeasonalLogoResult(entry: SeasonalLogoEntry): SeasonalLogoResult {
    return {
        altText: entry.altText,
        src: entry.asset,
        countries: getThemeCountries({
            matcher: entry.matcher,
            countries: entry.countries,
            countryGroups: entry.countryGroups,
        }),
    };
}

const DEFAULT_LOGO_ENTRY: SeasonalLogoEntry = {
    altText: 'Omnidots logo',
    matcher: () => true,
    asset: baseLogo,
};

export const DEFAULT_SEASONAL_LOGO_RESULT = toSeasonalLogoResult(DEFAULT_LOGO_ENTRY);

export const HOLIDAY_LOGOS: SeasonalLogoEntry[] = HOLIDAY_THEMES.map(toSeasonalLogoEntry).filter(
    Boolean
) as SeasonalLogoEntry[];

export function getSeasonalLogoDecision(context: SeasonalLogoContext): SeasonalLogoResult | null {
    const theme = getSeasonalThemeDecision(context, (entry) => Boolean(entry.logo));
    if (!theme?.logo) {
        return null;
    }

    return {
        altText: theme.logo.altText,
        src: theme.logo.asset,
        countries: getThemeCountries(theme),
    };
}

export function useSeasonalLogo(): SeasonalLogoResult | null {
    return useMemo(
        () =>
            getSeasonalLogoDecision({
                date: new Date(),
                timezone: getBrowserTimezone(),
            }),
        []
    );
}
